import React, { useState, useCallback, useRef, useEffect } from "react";
import "./input-field.scss";

export type DateFormat =
  | "dd-mm-yyyy"
  | "mm-dd-yyyy"
  | "yyyy-mm-dd"
  | "dd/mm/yyyy"
  | "mm/dd/yyyy"
  | "yyyy/mm/dd";

export interface InputFieldProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "size"> {
  label?: string;
  labelAddon?: React.ReactNode;
  hint?: string;
  error?: string;
  requiredError?: string;
  size?: "sm" | "md" | "lg";
  icon?: React.ReactNode;
  fullWidth?: boolean;
  labelClassName?: string;
  inputClassName?: string;
  dateFormat?: DateFormat;
  onDateChange?: (formattedDate: string, isoDate: string) => void;
}

function formatIsoDate(iso: string, fmt: DateFormat): string {
  if (!iso) return "";
  const [yyyy, mm, dd] = iso.split("-");
  if (!yyyy || !mm || !dd) return iso;
  const sep = fmt.includes("/") ? "/" : "-";
  if (fmt.startsWith("dd")) return `${dd}${sep}${mm}${sep}${yyyy}`;
  if (fmt.startsWith("mm")) return `${mm}${sep}${dd}${sep}${yyyy}`;
  return `${yyyy}${sep}${mm}${sep}${dd}`;
}

/**
 * Parses an incoming date string into ISO format (YYYY-MM-DD).
 * Accepts either an already-ISO value or a string matching the given `fmt`.
 * Returns "" if the input cannot be parsed.
 */
function parseToIso(input: unknown, fmt: DateFormat): string {
  if (typeof input !== "string" || input === "") return "";

  const isoMatch = input.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (isoMatch) return input;

  const sep = fmt.includes("/") ? "/" : "-";
  const parts = input.split(sep);
  if (parts.length !== 3) return "";
  const [a, b, c] = parts;
  if (!a || !b || !c) return "";

  if (fmt.startsWith("dd")) return `${c}-${b}-${a}`;
  if (fmt.startsWith("mm")) return `${c}-${a}-${b}`;
  return `${a}-${b}-${c}`;
}

function CalendarIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M12.667 2.667H3.333C2.597 2.667 2 3.264 2 4v9.333c0 .737.597 1.334 1.333 1.334h9.334c.736 0 1.333-.597 1.333-1.334V4c0-.736-.597-1.333-1.333-1.333ZM10.667 1.333V4M5.333 1.333V4M2 6.667h12"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

let idCounter = 0;

const InputField = ({
  label,
  labelAddon,
  hint,
  error,
  requiredError = "This field is required",
  size = "sm",
  icon,
  fullWidth = false,
  labelClassName,
  inputClassName,
  className,
  disabled,
  required,
  id,
  dateFormat,
  onDateChange,
  ...rest
}: InputFieldProps) => {
  const [fallbackId] = useState(() => `input-field-${++idCounter}`);
  const inputId = id || fallbackId;
  const [focused, setFocused] = useState(false);
  const [touched, setTouched] = useState(false);
  const [internalIso, setInternalIso] = useState(() =>
    dateFormat
      ? parseToIso(rest.value ?? rest.defaultValue, dateFormat)
      : "",
  );
  const fieldRef = useRef<HTMLDivElement>(null);
  const dateRef = useRef<HTMLInputElement>(null);

  const isDateMode = !!dateFormat;
  const cls = "input-field";

  useEffect(() => {
    if (!isDateMode || rest.value === undefined) return;
    const nextIso = parseToIso(rest.value, dateFormat!);
    setInternalIso((prev) => (prev === nextIso ? prev : nextIso));
  }, [rest.value, dateFormat, isDateMode]);

  const displayValue = isDateMode
    ? formatIsoDate(internalIso, dateFormat!)
    : undefined;

  const value = isDateMode
    ? displayValue ?? ""
    : (rest.value ?? rest.defaultValue ?? "");
  const isEmpty = typeof value === "string" ? value.trim() === "" : !value;
  const requiredValidationError = required && touched && isEmpty ? requiredError : undefined;
  const resolvedError = error || requiredValidationError;
  const hasError = !!resolvedError;

  const rootClass = [
    cls,
    `${cls}--${size}`,
    fullWidth ? `${cls}--full-width` : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const fieldClass = [
    `${cls}__field`,
    focused ? `${cls}__field--focused` : "",
    hasError ? `${cls}__field--error` : "",
    disabled ? `${cls}__field--disabled` : "",
  ]
    .filter(Boolean)
    .join(" ");

  const handleFocus = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      setFocused(true);
      rest.onFocus?.(e);
    },
    [rest.onFocus],
  );

  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      setFocused(false);
      if (required) setTouched(true);
      rest.onBlur?.(e);
    },
    [rest.onBlur, required],
  );

  const handleFieldClick = useCallback(() => {
    if (isDateMode && !disabled) {
      dateRef.current?.showPicker?.();
      return;
    }
    fieldRef.current?.querySelector("input")?.focus();
  }, [isDateMode, disabled]);

  const handleDateChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const iso = e.target.value;
      setInternalIso(iso);
      if (required && !iso) setTouched(true);
      onDateChange?.(formatIsoDate(iso, dateFormat!), iso);
    },
    [dateFormat, onDateChange, required],
  );

  const message = hasError ? resolvedError : hint;
  const messageClass = hasError ? `${cls}__error` : `${cls}__hint`;

  return (
    <div className={rootClass}>
      {(label != null || labelAddon != null || required) && (
        <div className={`${cls}__label-row`}>
          {label != null && (
            <label
              htmlFor={inputId}
              className={[`${cls}__label`, labelClassName]
                .filter(Boolean)
                .join(" ")}
            >
              {label}
              {required && (
                <span className={`${cls}__required`} aria-hidden="true"> *</span>
              )}
            </label>
          )}
          {labelAddon != null && (
            <span className={`${cls}__label-addon`}>{labelAddon}</span>
          )}
        </div>
      )}
      <div
        ref={fieldRef}
        className={fieldClass}
        onClick={handleFieldClick}
      >
        {isDateMode ? (
          <>
            <input
              id={inputId}
              className={[`${cls}__input`, inputClassName]
                .filter(Boolean)
                .join(" ")}
              type="text"
              readOnly
              disabled={disabled}
              required={required}
              value={displayValue}
              placeholder={rest.placeholder || dateFormat}
              onFocus={handleFocus}
              onBlur={handleBlur}
            />
            <input
              ref={dateRef}
              type="date"
              className={`${cls}__date-hidden`}
              tabIndex={-1}
              aria-hidden="true"
              disabled={disabled}
              value={internalIso}
              min={rest.min as string}
              max={rest.max as string}
              onChange={handleDateChange}
            />
          </>
        ) : (
          <input
            id={inputId}
            className={[`${cls}__input`, inputClassName]
              .filter(Boolean)
              .join(" ")}
            disabled={disabled}
            required={required}
            onFocus={handleFocus}
            onBlur={handleBlur}
            {...rest}
          />
        )}
        {(icon != null || isDateMode) && (
          <span className={`${cls}__icon`} aria-hidden="true">
            {icon ?? <CalendarIcon />}
          </span>
        )}
      </div>
      {message != null && (
        <span className={messageClass}>{message}</span>
      )}
    </div>
  );
};

export { InputField };
export default InputField;
